// v0.6 — Inline "Amend VAT number → live VIES recheck" for rows flagged
// VAT_NUMBER_INVALID or VAT_CHECK_UNAVAILABLE. The operator types a
// corrected buyer VAT number + a reason; the backend runs a LIVE VIES/HMRC
// check for immediate feedback, records the correction as an override (the
// uploaded number is kept intact) + an audit doc, and the next re-run
// (one for every answer, SavedAnswersBar) reclassifies from the corrected value.
//   * VALID       → saved; the row will route B2B_ICS at the next re-run.
//   * INVALID     → still not a valid number; keep trying (refuse to guess).
//   * UNAVAILABLE → VIES unreachable; recorded, but keep the row pending
//                   and retry later — never auto-routed.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { amendVatNumber } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { VAT_NUMBER_AMEND_REASONS } from "../../lib/reviewReasonPresets";

const STATUS_STYLE = {
  VALID: "text-emerald-700 dark:text-emerald-300",
  INVALID: "text-red-700 dark:text-red-300",
  UNAVAILABLE: "text-amber-700 dark:text-amber-300",
  FORMAT_VALID: "text-amber-700 dark:text-amber-300",
  OUT_OF_SCOPE: "text-slate-600 dark:text-slate-300",
};

export const VatNumberAmend = ({ tid, calcId, onResolved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [vat, setVat] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => {
    setVat(""); setReason(""); setPreset(""); setErr(null); setResult(null);
  };

  const cleanedVat = vat.trim().toUpperCase().replace(/\s+/g, "");
  const cleanedReason = reason.trim();
  const valid = cleanedVat.length >= 4 && cleanedReason.length >= 10
    && !/[<>]/.test(cleanedReason);

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const res = await amendVatNumber(calcId, {
        txn_id: tid, corrected_vat_number: cleanedVat, reason: cleanedReason,
      });
      setResult(res);
      // the corrected number is saved whatever the check says (and used at
      // the next re-run), so the re-run bar learns of it every time (review 2026-11-01)
      if (res.vies_status !== "VALID" && onResolved) await onResolved();
      if (res.vies_status === "VALID") {
        toast.success(
          t("ui.review_grouped.amend_toast_valid", { defaultValue: "VAT number valid — figures change at the next re-run" }),
          { description: `${cleanedVat}` });
        setOpen(false); reset();
        // A1-105 — saved for the one re-run, like every Step 4 answer
        if (onResolved) await onResolved();
      } else if (res.unavailable) {
        toast.warning(
          t("ui.review_grouped.amend_toast_unavail", { defaultValue: "VIES unreachable — row kept pending, retry later" }));
      } else {
        toast.error(
          t("ui.review_grouped.amend_toast_invalid", { defaultValue: "VAT number still not valid" }),
          { description: `${cleanedVat} → ${res.vies_status}` });
      }
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(
        t("ui.review_grouped.amend_toast_fail", { defaultValue: "Could not amend VAT number" }),
        { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`amend-vat-open-${tid}`}
          className="text-[10px] tabular-nums px-2 py-1 rounded border border-teal-400 dark:border-teal-600 hover:bg-teal-100 dark:hover:bg-teal-900/40 text-teal-700 dark:text-teal-200"
        >
          {t("ui.review_grouped.amend_open_btn", { defaultValue: "Amend VAT number →" })}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`amend-vat-popover-${tid}`}
        className="w-96 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2"
      >
        <div className="text-[11px] font-bold text-slate-700 dark:text-slate-200">
          {t("ui.review_grouped.amend_title", { defaultValue: "Correct the buyer VAT number" })}
        </div>
        <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">
          {t("ui.review_grouped.amend_help", {
            defaultValue:
              "Type the corrected number and we'll re-check it against VIES/HMRC live. Your uploaded file is untouched — the correction is recorded as an amendment and applied on the next re-run.",
          })}
        </div>
        <input
          type="text"
          data-testid={`amend-vat-input-${tid}`}
          value={vat}
          onChange={(e) => setVat(e.target.value)}
          placeholder={t("ui.review_grouped.amend_vat_placeholder", { defaultValue: "e.g. DE123456789" })}
          className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 rounded px-2 py-1 text-xs tabular-nums uppercase"
        />
        {/* v0.6.12 A5-27 — the number stays typed (it is an open
            identifier); why it was wrong is picked. */}
        <PresetReasonSelect
          testid={`amend-vat-reason-preset-${tid}`}
          presets={VAT_NUMBER_AMEND_REASONS}
          preset={preset}
          onPick={(p, v) => { setPreset(p); setReason(v); }}
        />
        {result && (
          <div
            data-testid={`amend-vat-result-${tid}`}
            className={`text-[11px] font-semibold ${STATUS_STYLE[result.vies_status] || "text-slate-600"}`}
          >
            {t("ui.review_grouped.amend_result_label", { defaultValue: "VIES result" })}: {result.vies_status}
            {result.vies_source ? ` · ${result.vies_source}` : ""}
            {result.trader_name ? ` · ${result.trader_name}` : ""}
            {result.unavailable && (
              <div className="text-[10px] font-normal text-amber-700 dark:text-amber-300 mt-0.5">
                {t("ui.review_grouped.amend_unavail_hint", { defaultValue: "Recorded, but the row stays pending until VIES confirms — retry when reachable." })}
              </div>
            )}
          </div>
        )}
        {err && (
          <div data-testid={`amend-vat-error-${tid}`} className="text-[10px] text-red-700 dark:text-red-300">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid={`amend-vat-cancel-${tid}`}
            onClick={() => { setOpen(false); reset(); }}
            className="text-[10px] underline text-slate-600 dark:text-slate-300"
          >
            {t("ui.review_grouped.amend_cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            data-testid={`amend-vat-submit-${tid}`}
            data-resolution-class="self-resolve"
            data-resolution-endpoint="POST /api/runs/calc/{calc_id}/amend-vat-number"
            disabled={busy || !valid}
            onClick={submit}
            className="text-[10px] font-bold px-3 py-1 rounded bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white"
          >
            {busy
              ? t("ui.review_grouped.amend_submitting", { defaultValue: "Checking…" })
              : t("ui.review_grouped.amend_submit", { defaultValue: "Recheck & save" })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
    </>
  );
};

export default VatNumberAmend;
